import React from 'react';
import { useSelector } from 'react-redux';

import { emptyArray } from 'ui-components/default-props/empty';
import Global from '../../store/global-selectors';
import { getGroupedShippingServicesForType } from '../../store/shipping-service/group-selector';
import { groupByCarrierAndCustom } from '../../fulfillment-planner/package/dropdown/package-group-resolver';

const getConfirmationInfoFromCarrier = (selectedCarrier, confirmationId) => {
  if (!selectedCarrier.length) {
    return {
      confirmationName: '',
      confirmations: emptyArray,
    };
  }

  const { confirmations } = selectedCarrier[0];
  const foundConfirmation = confirmations.find((con) => con.confirmationId === confirmationId) || {};
  return {
    confirmations,
    confirmationName: foundConfirmation.name,
  };
};

const filterCarrierAndCustomPackages = (pack, selectedCarrierId) => {
  if (pack.isHidden) {
    return false;
  }
  return pack.isCustom || pack.carrierId === selectedCarrierId;
};

const getProductProfileNamesFromIds = (state, serviceId, packageId, confirmationId, providerId) => {
  const services = Global.selectSellerServices(state);
  const packages = Global.selectSellerPackages(state);
  const carriers = Global.selectReferenceDatagetSellerCarriers(state) || [];

  const foundService = services.find((s) => s.serviceId === serviceId && s.labelProviderId === providerId) || {};
  const foundPackage = packages.find((p) => p.packageTypeId === packageId && p.carrierId === foundService.carrierId) || {};

  const foundCarrier = (!!foundService.carrierId && carriers.find((c) => c.carrierId === foundService.carrierId)) || {
    confirmations: [],
  };
  const foundConfirmation = foundCarrier.confirmations.find((c) => c.confirmationId === confirmationId) || {};
  return {
    inheritedServiceName: foundService.name,
    inheritedPackageName: foundPackage.name,
    inheritedConfirmationName: foundConfirmation.name,
  };
};

/**
 * @param {object} props
 * @param {string} props.serviceId
 * @param {string} props.packageTypeId
 * @param {string} props.confirmationId
 * @param {string} [props.inheritedServiceId]
 * @param {string} [props.inheritedProviderId]
 * @param {string} [props.inheritedPackageTypeId]
 * @param {string} [props.inheritedConfirmationId]
 * @param {JSX.Element} props.children
 * @returns {JSX.Element}
 */
export const ServicesPackagesConfirmations = React.memo(
  ({
    serviceId,
    packageTypeId,
    confirmationId,
    inheritedServiceId = '',
    inheritedProviderId = '',
    inheritedPackageTypeId = '',
    inheritedConfirmationId = '',
    children,
  }) => {
    const allPackages = useSelector(Global.selectSellerPackages);
    const serviceGroups = useSelector((state) =>
      getGroupedShippingServicesForType('Provider', state, {
        selectedServiceId: serviceId,
      }),
    );
    const {
      name: selectedServiceName,
      carrierId: selectedCarrierId,
      labelProviderId: selectedProviderId,
    } = useSelector(Global.selectSellerServices).find((ser) => ser.serviceId === serviceId) || {};
    const sellerCarriers = useSelector(Global.selectReferenceDatagetSellerCarriers) || [];
    const selectedCarrierArray = sellerCarriers.filter((sc) => sc.carrierId === selectedCarrierId);

    const { confirmationName, confirmations } = getConfirmationInfoFromCarrier(selectedCarrierArray, confirmationId);

    const { name: selectedPackageName } = allPackages.find((pack) => pack.packageTypeId === packageTypeId) || {};
    const packages = allPackages.filter((pack) => filterCarrierAndCustomPackages(pack, selectedCarrierId));
    const { inheritedServiceName, inheritedPackageName, inheritedConfirmationName } = useSelector((state) =>
      getProductProfileNamesFromIds(state, inheritedServiceId, inheritedPackageTypeId, inheritedConfirmationId, inheritedProviderId),
    );
    return (
      <>
        {children({
          serviceGroups,
          confirmations: confirmations ?? emptyArray,
          selectedConfirmationName: confirmationName,
          selectedServiceName,
          selectedProviderId,
          selectedCarrierId,
          selectedPackageName,
          packages: packages ?? emptyArray,
          packageGroups: groupByCarrierAndCustom(packages, selectedCarrierArray) ?? emptyArray,
          inheritedServiceName,
          inheritedPackageName,
          inheritedConfirmationName,
        })}
      </>
    );
  },
);
